import { SESSION_RECORDING_EVENTS, sendSessionRecordingEvent } from "utils/session-recording"

const CONTAINER_SELECTOR = ".forecast-text-summary"
const READING_DWELL_MS = 3000

function summaryContainer(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null

  return target.closest(CONTAINER_SELECTOR)
}

function clickEventName(target: Element): string {
  const readMoreButton = target.closest<HTMLElement>("[data-read-more]")
  if (!readMoreButton) return SESSION_RECORDING_EVENTS.forecastSummaryClick

  // The read-more handler flips data-opened asynchronously, so at click time
  // it still holds the pre-toggle state.
  return readMoreButton.dataset.opened === "true"
    ? SESSION_RECORDING_EVENTS.forecastSummaryCollapse
    : SESSION_RECORDING_EVENTS.forecastSummaryExpand
}

// Reports the pointer staying over the forecast summary for long enough to
// suggest the visitor is reading it. Hover time is accumulated across
// re-entries while the tab is visible, and the event is sent at most once
// per page view. Listeners are delegated to the document because the summary
// container is replaced when the units are switched.
class ReadingTracker {
  private tracked = false
  private hovered = false
  private remainingMs = READING_DWELL_MS
  private resumedAt = 0
  private timerId: number | null = null

  enter() {
    this.hovered = true
    this.resume()
  }

  leave() {
    this.hovered = false
    this.pause()
  }

  visibilityChanged() {
    if (document.visibilityState === "visible") {
      this.resume()
    } else {
      this.pause()
    }
  }

  private resume() {
    if (this.tracked || this.timerId !== null) return
    if (!this.hovered || document.visibilityState !== "visible") return

    this.resumedAt = Date.now()
    this.timerId = window.setTimeout(() => {
      this.timerId = null
      this.tracked = true
      sendSessionRecordingEvent(SESSION_RECORDING_EVENTS.forecastSummaryReading)
    }, this.remainingMs)
  }

  private pause() {
    if (this.timerId === null) return

    window.clearTimeout(this.timerId)
    this.timerId = null
    this.remainingMs = Math.max(this.remainingMs - (Date.now() - this.resumedAt), 0)
  }
}

export default function initForecastSummarySessionRecording() {
  const readingTracker = new ReadingTracker()

  document.addEventListener("click", (event) => {
    const container = summaryContainer(event.target)
    if (!container) return

    sendSessionRecordingEvent(clickEventName(event.target as Element))
  })

  document.addEventListener("pointerover", (event) => {
    if (!summaryContainer(event.target)) return
    if (summaryContainer(event.relatedTarget)) return

    readingTracker.enter()
  })

  document.addEventListener("pointerout", (event) => {
    if (!summaryContainer(event.target)) return
    if (summaryContainer(event.relatedTarget)) return

    readingTracker.leave()
  })

  document.addEventListener("visibilitychange", () => readingTracker.visibilityChanged())
}
